/**
 * Composant pour gérer la configuration Stripe Connect d'une organisation
 * Utilise des liens d'inscription d'entreprise Stripe Connect
 */

import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Info, ArrowLeft, AlertTriangle } from "lucide-react";
import StripeHostedOnboardingSimple from "@/components/stripe/StripeHostedOnboardingSimple";
import StripeApiKeyConfig from "./StripeApiKeyConfig";
import PayPalApiKeyConfig from "./PayPalApiKeyConfig";
import PaypalExtensionConfig from "./PaypalExtensionConfig";
import { OrgaConfigurationRepository } from "@/services/firebase/orga-configuration-repository";
import { useUserData } from "@/shared/hooks/useUserData";
import { useOrgaConfigurationContext } from "@/app/providers";
import { Card, CardBody, Badge, Button } from "@/components/ui";
import { PAYMENT_PROVIDERS, PaymentMode, isProviderConfigured, hasStripeConnectAccount } from "@/shared/config/paymentProviders";
import { CONTACT_INFO } from "@/shared/constants/contact";



interface StripeConnectManagerProps {
  organizationId: string;
}

// Interface pour typer proprement les providers (évite les erreurs de typage TS sur les propriétés optionnelles)



const StripeConnectManager: React.FC<StripeConnectManagerProps> = ({
  organizationId,
}) => {
  const { t } = useTranslation();
  const [isCommissionLoading, setIsCommissionLoading] = useState(false);
  const { isSuperAdmin } = useUserData();

  // Récupérer l'orgConfig complète pour les vérifications de configuration
  const { configuration: orgConfig } = useOrgaConfigurationContext();

  // État local pour le mode de paiement, initialisé depuis la config ou 'stripe_connect' par défaut
  const [selectedMode, setSelectedMode] = useState<PaymentMode>(
    orgConfig?.paymentMode || 'none'
  );

  // Mettre à jour le mode sélectionné quand la config change
  useEffect(() => {
    if (orgConfig?.paymentMode) {
      setSelectedMode(orgConfig.paymentMode);
    }
  }, [orgConfig?.paymentMode]);

  // Utiliser le hash URL pour la navigation (permet le bouton retour et la persistance après refresh)
  const getProviderFromHash = (): string | null => {
    const hash = window.location.hash.replace('#', '');
    // Vérifier si c'est un provider valide (payment-connect ou payment-apikey ou payment-paypal)
    if (hash === 'payment-connect') return 'stripe_connect';
    if (hash === 'payment-apikey') return 'stripe_api_key';
    if (hash === 'payment-paypal') return 'paypal';
    return null;
  };

  const [configuringProviderId, setConfiguringProviderId] = useState<string | null>(getProviderFromHash());

  // Synchroniser avec le hash URL
  useEffect(() => {
    const handleHashChange = () => {
      const providerId = getProviderFromHash();
      setConfiguringProviderId(providerId);
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Navigation vers un provider avec mise à jour du hash
  const navigateToProvider = (providerId: string) => {
    let hashSuffix = 'connect';
    if (providerId === 'stripe_api_key') hashSuffix = 'apikey';
    if (providerId === 'paypal') hashSuffix = 'paypal';

    window.location.hash = `payment-${hashSuffix}`;
    setConfiguringProviderId(providerId);
  };

  // Retour à la liste des providers
  const navigateBack = () => {
    window.location.hash = 'payment';
    setConfiguringProviderId(null);
  };

  // Fonction pour changer le mode de paiement avec sauvegarde
  const handleModeChange = async (mode: PaymentMode) => {
    if (mode === selectedMode || mode === 'none') return;

    try {
      await OrgaConfigurationRepository.update(organizationId, {
        // Root level paymentMode (provider-agnostic)
        paymentMode: mode,
      });

      setSelectedMode(mode);
      const modeLabel = mode === 'stripe_connect' ? 'Stripe Connect' : (mode === 'paypal' ? 'PayPal' : 'Clé API Stripe');
      toast.success(`Mode ${modeLabel} activé`);
    } catch (error) {
      console.error("❌ Erreur lors du changement de mode:", error);
      toast.error("Erreur lors de la sauvegarde du mode");
    }
  };

  // Fonction pour mettre à jour les taux de commission (pourcentage et fixe)
  const handleUpdateCommission = async (newRate: number, newFixedCommission?: number) => {
    setIsCommissionLoading(true);
    try {
      const currentCommission = orgConfig?.commissionConfig || { commissionRate: 2.8, fixedCommission: 0 };

      const updatedCommission = {
        commissionRate: newRate,
        fixedCommission: newFixedCommission !== undefined ? newFixedCommission : currentCommission.fixedCommission,
      };

      await OrgaConfigurationRepository.update(organizationId, {
        commissionConfig: updatedCommission,
      });

      const fixedInEuros = (newFixedCommission !== undefined ? newFixedCommission : currentCommission.fixedCommission || 0) / 100;
      toast.success(t('admin.configuration.stripe.commissionUpdated', { rate: newRate, fixed: fixedInEuros.toFixed(2) }));
    } catch (error) {
      console.error("❌ Erreur lors de la mise à jour de la commission:", error);
      toast.error(t('admin.configuration.stripe.updateError'));
      throw error;
    } finally {
      setIsCommissionLoading(false);
    }
  };


  // --- VUE CONFIGURATION (DETAIL) ---
  if (configuringProviderId) {
    const provider = PAYMENT_PROVIDERS.find(p => p.id === configuringProviderId);
    if (!provider) return null;

    return (
      <div className="space-y-6 min-h-[600px] animate-in slide-in-from-right-4 fade-in duration-300">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 bg-primary/10 rounded-xl flex-shrink-0">
              <provider.icon className="w-6 h-6 sm:w-8 h-8 text-primary" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">Configuration {provider.title}</h2>
              <p className="text-xs sm:text-sm text-foreground-secondary mt-0.5 sm:mt-1">Configurez les paramètres pour ce mode d'encaissement.</p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={navigateBack}
            leftIcon={<ArrowLeft className="w-4 h-4 sm:w-5 h-5 group-hover:-translate-x-1 transition-transform" />}
            className="text-foreground-secondary hover:text-foreground group self-start sm:self-auto flex-shrink-0"
          >
            <span className="text-sm">Retour</span>
            <span className="hidden md:inline text-sm ml-1">aux modes de paiement</span>
          </Button>
        </div>

        {configuringProviderId === 'stripe_api_key' && (
          <StripeApiKeyConfig
            organizationId={organizationId}
            stripeSecretKey={orgConfig?.stripeApiKeyConfig?.secretKeyMasked}
            stripePublishableKey={orgConfig?.stripeApiKeyConfig?.publishableKey}
            stripeApiKeyWebhookSecret={orgConfig?.stripeApiKeyConfig?.webhookSecretMasked}
            paypalEnabled={orgConfig?.stripeApiKeyConfig?.paypalEnabled}
            lastWebhookReceivedAt={orgConfig?.stripeApiKeyConfig?.lastWebhookReceivedAt}
            onConfigChange={async (/* config */) => {
              // La sauvegarde est déjà faite côté serveur par la Cloud Function
              // Le listener temps réel du hook useOrgaConfiguration mettra à jour l'UI automatiquement
              setSelectedMode('stripe_api_key');
            }}
          />
        )}

        {configuringProviderId === 'paypal' && (
          <PayPalApiKeyConfig
            organizationId={organizationId}
            config={orgConfig?.paypalConfig}
            onRefresh={() => {
              // Optionnel: rafraîchir d'autres données si nécessaire
            }}
          />
        )}

        {configuringProviderId === 'stripe_connect' && (
          <div className="space-y-4">
            {orgConfig && !hasStripeConnectAccount(orgConfig) && (
              <Card variant="default" className="bg-info-5 border-info-20">
                <CardBody>
                  <div className="flex items-start">
                    <Info className="w-5 h-5 text-info mr-3 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-info-dark">
                        <strong>{t('admin.configuration.stripe.firstStep')}</strong> {t('admin.configuration.stripe.firstStepDesc')}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            )}
            <StripeHostedOnboardingSimple
              organizationId={organizationId}
              stripeAccountId={orgConfig?.stripeConnectConfig?.accountId || orgConfig?.stripeConfig?.stripeAccountId}
              stripeStatus={orgConfig?.stripeConnectConfig?.status || orgConfig?.stripeConfig?.stripeStatus}
              onUpdateCommission={handleUpdateCommission}
              isCommissionLoading={isCommissionLoading}
              isSuperAdmin={isSuperAdmin}
              onStatusChange={async (status) => {

                try {
                  await OrgaConfigurationRepository.update(organizationId, {
                    // Nouvelle architecture - mise à jour du statut
                    'stripeConnectConfig.status': status,
                    isStripeEnabled: status === 'active',
                  } as any);
                  // Laisser onStripeConfigChange pour l'instant si d'autres composants en dépendent
                  // mais on ne sauve plus dans le map global legacy
                } catch (error) {
                  toast.error(t('admin.configuration.stripe.statusUpdateError'));
                }
              }}
              onAccountCreated={async (accountId) => {

                try {
                  await OrgaConfigurationRepository.update(organizationId, {
                    // Nouvelle architecture
                    paymentMode: 'stripe_connect',
                    stripeConnectConfig: {
                      accountId: accountId,
                      status: 'pending',
                      chargesEnabled: false,
                      payoutsEnabled: false,
                      detailsSubmitted: false,
                      autoTransferEnabled: true,
                      transferDelay: 0,
                      configuredAt: new Date().toISOString(),
                      paypalEnabled: false,
                    },
                  });
                  setSelectedMode('stripe_connect');
                } catch (error) {
                  toast.error(t('admin.configuration.stripe.accountSaveError'));
                }
              }}
              onAccountInfoRetrieved={async (accountInfo) => {
                const isVerified = accountInfo.charges_enabled && accountInfo.payouts_enabled && accountInfo.details_submitted;

                try {
                  await OrgaConfigurationRepository.update(organizationId, {
                    // Nouvelle architecture
                    paymentMode: 'stripe_connect',
                    stripeConnectConfig: {
                      ...orgConfig?.stripeConnectConfig,
                      accountId: accountInfo.id,
                      status: isVerified ? 'active' : 'pending',
                      chargesEnabled: accountInfo.charges_enabled || false,
                      payoutsEnabled: accountInfo.payouts_enabled || false,
                      detailsSubmitted: accountInfo.details_submitted || false,
                      autoTransferEnabled: true,
                      transferDelay: 0,
                      dashboardUrl: accountInfo.dashboard_url,
                      configuredAt: new Date().toISOString(),
                      lastUpdatedAt: new Date().toISOString(),
                    } as any,
                    isStripeEnabled: isVerified,
                  });
                  setSelectedMode('stripe_connect');
                  toast.success(isVerified ? t('admin.configuration.stripe.accountActivated') : t('admin.configuration.stripe.accountConfigured'));
                } catch (error) {
                  toast.error(t('admin.configuration.stripe.infoSaveError'));
                }
              }}
            />

            <PaypalExtensionConfig
              organizationId={organizationId}
              id="stripe_connect_onboarding"
              paypalEnabled={orgConfig?.stripeConnectConfig?.paypalEnabled || false}
              onChange={async (config) => {
                try {
                  await OrgaConfigurationRepository.update(organizationId, {
                    'stripeConnectConfig.paypalEnabled': config.paypalEnabled,
                  } as any);
                  toast.success(config.paypalEnabled ? "PayPal activé pour Stripe Connect" : "PayPal désactivé");
                } catch (error) {
                  console.error("Erreur lors de la mise à jour des options PayPal:", error);
                  toast.error("Erreur lors de la sauvegarde");
                }
              }}
            />
          </div>
        )}
      </div>
    );
  }

  // --- VUE LISTE (PRINCIPALE) ---
  return (
    <div className="space-y-6 min-h-[600px]">
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-foreground">Mode d'encaissement</h2>
          <p className="text-sm text-foreground-secondary mt-1">
            Choisissez comment vous souhaitez recevoir vos paiements et gérer vos commissions.
          </p>
        </div>

        {/* Message d'assistance concis */}
        <Card variant="default" className="bg-info-5 border-info-20">
          <CardBody className="py-3 px-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-info flex-shrink-0 mt-0.5" />
                <p className="text-sm text-info-dark">
                  Besoin d'aide ou d'un mode de paiement spécifique ? Notre équipe vous accompagne pour une configuration personnalisée.
                </p>
              </div>
              <a
                href={`tel:${CONTACT_INFO.PHONE}`}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-info text-white rounded-lg text-xs font-bold hover:bg-info-dark transition-colors whitespace-nowrap self-start sm:self-auto"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                {CONTACT_INFO.PHONE_DISPLAY}
              </a>
            </div>
          </CardBody>
        </Card>

        <div className="space-y-4">
          {PAYMENT_PROVIDERS
            // Filtrer Stripe Connect pour les non-super admins
            .filter(provider => {
              if (provider.id === 'stripe_connect') {
                return isSuperAdmin;
              }
              return true;
            })
            .map((provider) => {
              const isSelected = selectedMode === provider.id;
              const isConfigured = isProviderConfigured(provider.id, orgConfig ?? undefined);

              return (
                <div key={provider.id} className={`rounded-xl transition-all ${isSelected ? 'ring-2 ring-primary/20 bg-background' : ''}`}>
                  <div
                    className={`transition-all rounded-xl border-2 group ${isSelected ? 'border-primary' : 'border-transparent hover:border-border-medium'
                      }`}
                  >
                    <Card variant={isSelected ? 'default' : 'outlined'} className="border-0">
                      <CardBody className="p-4 sm:p-5">
                        <div className="flex flex-col sm:flex-row gap-4">
                          {/* Icône et contenu principal */}
                          <div className="flex gap-3 sm:gap-4 flex-1 min-w-0">
                            <div className={`p-2.5 sm:p-3 rounded-xl flex-shrink-0 self-start ${isSelected ? 'bg-primary text-white' : 'bg-background-tertiary text-foreground-secondary'}`}>
                              <provider.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                            </div>

                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
                                <h3 className="font-bold text-foreground text-base sm:text-lg">{provider.title}</h3>
                                {isConfigured && <Badge variant="success" size="sm" className="flex-shrink-0">Configuré</Badge>}
                                {!isConfigured && isSelected && <Badge variant="warning" size="sm" className="flex-shrink-0">Configuration requise</Badge>}
                              </div>
                              <p className="text-xs sm:text-sm text-foreground-secondary">{provider.description}</p>

                              <ul className="mt-2 sm:mt-3 space-y-1 sm:space-y-1.5">
                                {provider.details?.map((detail, i) => (
                                  <li key={i} className="text-[11px] sm:text-xs text-foreground-secondary flex items-start">
                                    <span className="mr-1.5 sm:mr-2 text-primary">•</span>
                                    {detail}
                                  </li>
                                ))}
                              </ul>

                              {!isConfigured && isSelected && (
                                <div className="mt-3 sm:mt-4 p-2.5 sm:p-3 bg-warning/5 border border-warning/20 rounded-lg">
                                  <div className="flex items-center gap-2 mb-1.5 sm:mb-2">
                                    <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-warning" />
                                    <span className="text-[10px] sm:text-xs font-bold text-warning uppercase tracking-wide">Pré-requis</span>
                                  </div>
                                  <ul className="space-y-0.5 sm:space-y-1">
                                    {provider.id === 'stripe_connect' && (
                                      <>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          Informations de l'entreprise
                                        </li>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          Coordonnées bancaires (IBAN)
                                        </li>
                                      </>
                                    )}
                                    {provider.id === 'stripe_api_key' && (
                                      <>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          Clé Secrète Stripe (sk_...)
                                        </li>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          Clé Publique Stripe (pk_...)
                                        </li>
                                      </>
                                    )}
                                    {provider.id === 'paypal' && (
                                      <>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          PayPal Client ID
                                        </li>
                                        <li className="text-[11px] sm:text-xs text-foreground-secondary flex items-center">
                                          <span className="w-1 h-1 rounded-full bg-warning mr-1.5 sm:mr-2" />
                                          PayPal Client Secret
                                        </li>
                                      </>
                                    )}
                                  </ul>
                                </div>
                              )}

                              {/* Option d'extension PayPal pour tous les providers compatibles (sauf PayPal lui-même) */}
                              {provider.id !== 'paypal' && provider.id !== 'none' && (
                                <div className="mt-3 sm:mt-4 animate-in fade-in duration-300">
                                  <PaypalExtensionConfig
                                    organizationId={organizationId}
                                    id={provider.id}
                                    paypalEnabled={
                                      (() => {
                                        const configMap: Record<string, any> = {
                                          'stripe_connect': orgConfig?.stripeConnectConfig,
                                          'stripe_api_key': orgConfig?.stripeApiKeyConfig,
                                          'sumup': orgConfig?.sumupConfig,
                                          'stancer': orgConfig?.stancerConfig,
                                          'klarna': orgConfig?.klarnaConfig,
                                        };
                                        return configMap[provider.id]?.paypalEnabled || false;
                                      })()
                                    }
                                    onChange={async (config) => {
                                      try {
                                        const configKeyMap: Record<string, string> = {
                                          'stripe_connect': 'stripeConnectConfig',
                                          'stripe_api_key': 'stripeApiKeyConfig',
                                          'sumup': 'sumupConfig',
                                          'stancer': 'stancerConfig',
                                          'klarna': 'klarnaConfig',
                                        };

                                        const configKey = configKeyMap[provider.id];
                                        if (!configKey) return;

                                        await OrgaConfigurationRepository.update(organizationId, {
                                          [`${configKey}.paypalEnabled`]: config.paypalEnabled,
                                        } as any);
                                        toast.success(config.paypalEnabled ? "PayPal activé comme option" : "PayPal désactivé");
                                      } catch (error) {
                                        console.error("Erreur mise à jour PayPal extension:", error);
                                        toast.error("Erreur lors de la sauvegarde");
                                      }
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Actions - Responsive: colonne sur mobile, ligne sur desktop */}
                          <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-between gap-3 sm:gap-4 sm:ml-4 border-t sm:border-t-0 pt-3 sm:pt-0">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => navigateToProvider(provider.id)}
                              className="transition-opacity flex-1 sm:flex-none w-full sm:w-auto"
                            >
                              {isConfigured ? 'Paramètres' : 'Configurer'}
                            </Button>

                            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                              <span className="text-xs text-foreground-secondary sm:hidden">Activer</span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleModeChange(provider.id as PaymentMode);
                                }}
                                className={`w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all focus:outline-none focus:ring-2 focus:ring-primary/20 ${isSelected ? 'border-primary bg-primary' : 'border-border-dark hover:border-primary'
                                  }`}
                                role="radio"
                                aria-checked={isSelected}
                                title={isSelected ? "Sélectionné" : "Sélectionner ce mode"}
                              >
                                {isSelected && <div className="w-2.5 h-2.5 bg-white rounded-full shadow-sm" />}
                              </button>
                            </div>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
};

export default StripeConnectManager;
